import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';
import { Plus, PencilSimple, Trash } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useConfirm } from '../components/ConfirmProvider';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Printing = () => {
  const confirm = useConfirm();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    format: '',
    paper_type: '',
    is_color: 'false',
    price: ''
  });

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/printing`, { withCredentials: true });
      setConfigs(response.data);
    } catch (error) {
      console.error('Error fetching configs:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      is_color: formData.is_color === 'true',
      price: parseFloat(formData.price)
    };
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/printing/${editing.id}`, payload, { withCredentials: true });
        toast.success('Configuration mise à jour');
      } else {
        await axios.post(`${API_URL}/api/printing`, payload, { withCredentials: true });
        toast.success('Configuration créée');
      }
      setShowDialog(false);
      resetForm();
      fetchConfigs();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error(editing ? 'Erreur lors de la mise à jour' : 'Erreur lors de la création');
    }
  };

  const openEditDialog = (config) => {
    setEditing(config);
    setFormData({
      format: config.format || '',
      paper_type: config.paper_type || '',
      is_color: config.is_color ? 'true' : 'false',
      price: (config.price ?? '').toString(),
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!(await confirm('Êtes-vous sûr de vouloir supprimer cette configuration ?'))) return;
    try {
      await axios.delete(`${API_URL}/api/printing/${id}`, { withCredentials: true });
      toast.success('Configuration supprimée');
      fetchConfigs();
    } catch (error) {
      console.error('Error deleting config:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ format: '', paper_type: '', is_color: 'false', price: '' });
    setEditing(null);
  };

  return (
    <div className="App">
      <Sidebar />
      <div className="main-content" data-testid="printing-page">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">Impressions et objets associés</h1>
            <p className="text-slate-600">Configurez les tarifs d'impression</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowDialog(true); }}
            data-testid="create-printing-config-btn"
            className="bg-orange-500 hover:bg-orange-400 text-white"
          >
            <Plus size={20} weight="bold" className="mr-2" />
            Ajouter une configuration
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">Chargement...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Format</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Type de papier</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Couleur</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Prix</th>
                    <th className="text-right py-3 px-4 text-xs uppercase tracking-wider text-slate-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {configs.map((config) => (
                    <tr key={config.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium">{config.format}</td>
                      <td className="py-3 px-4 text-sm text-slate-600">{config.paper_type}</td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs ${
                          config.is_color ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'
                        }`}>
                          {config.is_color ? 'Couleur' : 'Monochrome'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-900">{config.price.toFixed(2)} €</td>
                      <td className="py-3 px-4 text-right">
                        <button
                          onClick={() => openEditDialog(config)}
                          data-testid={`edit-printing-config-${config.id}`}
                          className="inline-flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md mr-1"
                          aria-label="Modifier"
                        >
                          <PencilSimple size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(config.id)}
                          data-testid={`delete-printing-config-${config.id}`}
                          className="inline-flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md"
                          aria-label="Supprimer"
                        >
                          <Trash size={18} />
                        </button>
                      </td>
                    </tr>
                  ))}
                  {configs.length === 0 && (
                    <tr>
                      <td colSpan="5" className="py-12 text-center text-slate-500">
                        Aucune configuration enregistrée
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) resetForm(); }}>
          <DialogContent data-testid="printing-dialog">
            <DialogHeader>
              <DialogTitle>{editing ? 'Modifier' : 'Ajouter'} une configuration</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Format</label>
                <Input
                  value={formData.format}
                  onChange={(e) => setFormData({ ...formData, format: e.target.value })}
                  data-testid="printing-format-input"
                  placeholder="A4, A3, A5..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type de papier</label>
                <Input
                  value={formData.paper_type}
                  onChange={(e) => setFormData({ ...formData, paper_type: e.target.value })}
                  data-testid="printing-paper-type-input"
                  placeholder="Standard, Premium, Photo..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Type d'impression</label>
                <Select
                  value={formData.is_color}
                  onValueChange={(value) => setFormData({ ...formData, is_color: value })}
                  required
                >
                  <SelectTrigger data-testid="printing-color-select">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="false">Monochrome</SelectItem>
                    <SelectItem value="true">Couleur</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Prix (€)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  data-testid="printing-price-input"
                  required
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowDialog(false)}
                  data-testid="cancel-printing-btn"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  data-testid="submit-printing-btn"
                  className="bg-blue-600 hover:bg-blue-500 text-white"
                >
                  {editing ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Printing
